import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

// Single-select column filter: a funnel button in a table header that opens a
// dropdown of values. `value === ''` means no filter. `options` is an array of
// strings or { value, label } objects.
export default function ColumnFilter({ label, value, options, onChange }) {
  const { t } = useTranslation('common')
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  const active = !!value
  const selected = options.find(o => (typeof o === 'string' ? o : o.value) === value)
  const valueLabel = selected ? (typeof selected === 'string' ? selected : selected.label) : value
  useEffect(() => {
    if (!open) return
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])
  return (
    <span ref={ref} className="relative inline-flex align-middle">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o) }}
        className={`ml-1 inline-flex items-center justify-center w-5 h-5 rounded transition-colors ${
          active ? 'bg-gray-900 text-white' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'
        }`}
        title={active ? t('filter.filtered', { value: valueLabel }) : t('filter.filterBy', { label })}
        aria-label={t('filter.filterBy', { label })}
      >
        <svg width="10" height="10" viewBox="0 0 16 16" fill="currentColor">
          <path d="M1.5 2.5h13L9.5 9.2v4.3l-3 1.5V9.2L1.5 2.5z"/>
        </svg>
      </button>
      {open && (
        <div
          className="absolute z-30 top-6 left-0 min-w-[12rem] max-h-72 overflow-auto bg-white border border-gray-200 rounded-lg shadow-lg py-1"
          onClick={e => e.stopPropagation()}
        >
          <button
            onClick={() => { onChange(''); setOpen(false) }}
            className={`block w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 ${!value ? 'font-semibold text-gray-900' : 'text-gray-700'}`}
          >
            {t('filter.all', { label })}
          </button>
          <div className="my-1 border-t border-gray-100" />
          {options.length === 0 && (
            <div className="px-3 py-2 text-xs text-gray-400">{t('filter.noOptions')}</div>
          )}
          {options.map(opt => {
            const v = typeof opt === 'string' ? opt : opt.value
            const l = typeof opt === 'string' ? opt : opt.label
            return (
              <button
                key={v}
                onClick={() => { onChange(v); setOpen(false) }}
                className={`block w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 ${value === v ? 'font-semibold text-gray-900' : 'text-gray-700'}`}
              >
                {l}
              </button>
            )
          })}
        </div>
      )}
    </span>
  )
}
